import { mapCart, getOrCreateCartId, getOrCreateCart, getClosestVariant } from "utils/cart";
import { fetchRecommendedProducts } from "services/api/rebuy";
import getCurrency from "helpers/get-currency";
import { SHIPPING_PROTECTION_KEY, SHIPPING_PROTECTION } from "utils/constants";
import { isExperienceActive } from "helpers/convert";

const updateCart = async (set, get, cart) => {
	if (!cart?.id) return;

	const spLine = cart.lines.nodes.find(item => item.variant.product.title === SHIPPING_PROTECTION);

	// fix the check when experience (test) is active
	if (spLine && !isExperienceActive(["v.1004344032-g", "v.1004344033-g"])) {
		return removeCartItem(set, get, spLine.id);
	}

	if (isExperienceActive(["v.1004344032-g", "v.1004344033-g"])) {
		const protectionChanged = await syncShippingProtection(set, get, cart);
		if (protectionChanged) return;
	}

	const updatedCart = mapCart(cart, set);

	set(state => ({ cart: { ...state.cart, ...updatedCart, isUpdating: false, isLoading: false } }), false, "FETCH_CART");

	fetchRecommendedProducts(updatedCart, get().customer?.id).then(recommendations => {
		set(state => { state.cart.recommendations = recommendations || []; }, false, "FETCH_RECOMMENDATIONS");
	});

	return updatedCart;
};

export const addCartItem = async (set, get, variantId, quantity) => {
	try {
		const shopifyClient = get().shopifyClient;
		const cartId = await getOrCreateCartId(shopifyClient);
		const lineItems = [{ merchandiseId: variantId, quantity: quantity || 1 }];

		const updatedCart = await shopifyClient.cart.addLineItems(cartId, lineItems);
		return updateCart(set, get, updatedCart);
	} catch (err) {
		console.log({ err });
	}
};

export const updateCartItem = async (set, get, id, quantity, merchandiseId) => {
	try {
		set(state => { state.cart.isUpdating = true; }, false, "UPDATING_CART");
		const shopifyClient = get().shopifyClient;
		const cartId = await getOrCreateCartId(shopifyClient);
		const lineItem = { id, quantity };

		if (merchandiseId) {
			lineItem.merchandiseId = merchandiseId;
		}

		const updatedCart = await shopifyClient.cart.updateLineItems(cartId, [lineItem]);
		return updateCart(set, get, updatedCart);
	} catch (err) {
		console.log({ err });
	}
};

export const removeCartItem = async (set, get, id) => {
	try {
		const shopifyClient = get().shopifyClient;
		const cartId = await getOrCreateCartId(shopifyClient);
		const lineIds = [id];

		const updatedCart = await shopifyClient.cart.removeLineItems(cartId, lineIds);
		return updateCart(set, get, updatedCart);
	} catch (err) {
		console.log({ err });
	}
};

export const fetchCart = async (set, get) => {
	try {
		const shopifyClient = get().shopifyClient;
		let cart = await getOrCreateCart(shopifyClient);

		const { countryCode: customerCountryCode, code: customerCurrencyCode } = getCurrency();
		const currentCountryCode = cart.buyerIdentity?.countryCode;
		const cartCurrencyCode = cart.cost.subtotalAmount?.currencyCode;

		if (customerCountryCode !== currentCountryCode ||
			customerCurrencyCode !== cartCurrencyCode) {
			const response = await shopifyClient.cart.buyerIdentityUpdate(cart.id);
			if (response?.cart)
				cart = response.cart;
		}

		return updateCart(set, get, cart);

	}
	catch (err) {
		console.log("fetchCart", err);
	}
};

export const updateDiscountCodes = async (set, get, discountCodes) => {
	try {
		const shopifyClient = get().shopifyClient;
		const cartId = await getOrCreateCartId(shopifyClient);

		const updatedCart = await shopifyClient.cart.updateDiscountCodes(cartId, discountCodes);
		return updateCart(set, get, updatedCart);
	} catch (err) {
		console.log({ err });
	}
};

export const updateAttributes = async (set, get, attributes) => {
	try {
		const shopifyClient = get().shopifyClient;
		const cartId = await getOrCreateCartId(shopifyClient);

		const updatedCart = await shopifyClient.cart.attributesUpdate(cartId, attributes);
		return updateCart(set, get, updatedCart);
	} catch (err) {
		console.log({ err });
	}
};

export const initShippingProtection = async (set, get, variants, totalPrice) => {
	if (!variants?.length) return;

	const { customerCountry } = get().settings;
	const target = (totalPrice || 0) * 0.05;
	const designatedVariant = getClosestVariant(variants, customerCountry, target);

	set(state => {
		// Only set the designatedVariant if it's missing
		if (!state.cart.shippingProtection.designatedVariant) {
			state.cart.shippingProtection.designatedVariant = designatedVariant;
		}
		state.cart.shippingProtection.variants = variants;
	}, false, "SET_SHIPPING_PROTECTION_VARIANTS");
};

const syncShippingProtection = async (set, get, cart) => {
	const { customerCountry } = get().settings;
	const shopifyClient = get().shopifyClient;
	const { variants } = get().cart.shippingProtection;
	const lines = cart.lines.nodes;

	const spLines = lines.filter(line => line.variant.product.title === SHIPPING_PROTECTION);
	const nonSpLines = lines.filter(line => line.variant.product.title !== SHIPPING_PROTECTION);
	const hasSPAttr = cart.attributes.some(attr => attr.key === SHIPPING_PROTECTION_KEY);

	const spLine = spLines[0] || null;
	const currentPrice = parseFloat(spLine?.variant?.price?.amount || 0);
	const subtotal = parseFloat(cart.cost.subtotalAmount.amount);
	const target = (subtotal - currentPrice) * 0.05;
	const designated = getClosestVariant(variants, customerCountry, target);

	// Ensure only one shipping protection line item exists
	if (spLines.length > 1) {
		await Promise.all(spLines.slice(1).map(({ id }) => removeCartItem(set, get, id)));
	}

	// Remove SP if it's the only item
	if (spLine && nonSpLines.length === 0) {
		await removeCartItem(set, get, spLine.id);
		set(state => {
			state.cart.shippingProtection.selected = null;
			state.cart.shippingProtection.designatedVariant = null;
		});
		return true;
	}

	// Update SP if variant doesn't match the designated one
	const needsUpdate = spLine && spLine.variant.id !== designated?.gid;
	if (needsUpdate) {
		const sameProduct = designated?.productGid === spLine.variant.product.id;

		if (sameProduct) {
			await updateCartItem(set, get, spLine.id, 1, designated.gid);
		} else {
			await shopifyClient.cart.removeLineItems(cart.id, [spLine.id]);
			await addCartItem(set, get, designated.gid, 1);
		}
		return true;
	}

	// Auto-add SP if not present and eligible
	if (!spLine && !hasSPAttr && nonSpLines.length && designated) {
		await addCartItem(set, get, designated.gid, 1);
		return true;
	}

	// Final state sync
	set(state => {
		state.cart.shippingProtection.selected = spLine || null;
		state.cart.shippingProtection.designatedVariant = designated;
	});
};